import { useQuery } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { dashboardConfigApi } from '@/lib/api'
import { BaseWidget } from './BaseWidget'
import { FileSignature, AlertTriangle } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { ContractStatsData, ContractStatusCount, RecentContract } from '@/types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { PieChart, Pie, Cell, Tooltip } from 'recharts'

// Hook to get container dimensions using a callback ref
function useContainerDimensions() {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null)
  const [element, setElement] = useState<HTMLDivElement | null>(null)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const setRef = (node: HTMLDivElement | null) => {
    containerRef.current = node
    setElement(node)
  }

  useEffect(() => {
    if (!element) return

    let rafId: number
    let timeoutId: ReturnType<typeof setTimeout>

    const checkDimensions = () => {
      if (element) {
        const { offsetWidth, offsetHeight } = element
        if (offsetWidth > 0 && offsetHeight > 0) {
          setDimensions({ width: offsetWidth, height: offsetHeight })
        } else {
          timeoutId = setTimeout(() => {
            rafId = requestAnimationFrame(checkDimensions)
          }, 50)
        }
      }
    }

    checkDimensions()

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          setDimensions({ width: Math.floor(width), height: Math.floor(height) })
        }
      }
    })

    resizeObserver.observe(element)

    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
    }
  }, [element])

  return { containerRef: setRef, dimensions }
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#94a3b8',
  PENDING_SIGNATURE: '#f59e0b',
  SIGNED: '#22c55e',
  CANCELLED: '#6b7280',
  EXPIRED: '#ef4444',
}

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  DRAFT: 'secondary',
  PENDING_SIGNATURE: 'default',
  SIGNED: 'default',
  CANCELLED: 'outline',
  EXPIRED: 'destructive',
}

// Contract Stats Widget
export function ContractStatsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'CONTRACT_STATS'],
    queryFn: () => dashboardConfigApi.getWidgetData<ContractStatsData>('CONTRACT_STATS'),
  })

  const stats = data?.data

  return (
    <BaseWidget
      title={t('dashboard.contractStats')}
      icon={<FileSignature className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold">{stats?.total_contracts || 0}</p>
          <p className="text-xs text-muted-foreground">{t('contracts.total')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-500">{stats?.pending_signature || 0}</p>
          <p className="text-xs text-muted-foreground">{t('contracts.pending')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-500">{stats?.signed_contracts || 0}</p>
          <p className="text-xs text-muted-foreground">{t('contracts.signed')}</p>
        </div>
      </div>
    </BaseWidget>
  )
}

// Recent Contracts Widget
export function RecentContractsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'RECENT_CONTRACTS'],
    queryFn: () => dashboardConfigApi.getWidgetData<RecentContract[]>('RECENT_CONTRACTS'),
  })

  const contracts = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.recentContracts')}
      icon={<FileSignature className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {contracts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noRecentContracts')}</p>
        ) : (
          contracts.map((contract) => (
            <Link
              key={contract.contract_id}
              to={`/contracts/${contract.contract_id}`}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{contract.title}</p>
                <p className="text-xs text-muted-foreground truncate">{contract.client_name || '-'}</p>
              </div>
              <div className="text-right ml-2">
                <Badge variant={STATUS_VARIANTS[contract.status] || 'secondary'} className="text-xs">
                  {t(`contracts.status.${contract.status.toLowerCase()}`)}
                </Badge>
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

// Contracts by Status Widget
export function ContractsByStatusWidget() {
  const { t } = useTranslation()
  const { containerRef, dimensions } = useContainerDimensions()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'CONTRACTS_BY_STATUS'],
    queryFn: () => dashboardConfigApi.getWidgetData<ContractStatusCount[]>('CONTRACTS_BY_STATUS'),
  })

  const chartData = data?.data?.map(s => ({
    name: t(`contracts.status.${s.status.toLowerCase()}`),
    value: s.count,
    color: STATUS_COLORS[s.status] || '#94a3b8',
  })) || []

  const hasData = chartData.length > 0 && chartData.some(d => d.value > 0)

  return (
    <BaseWidget title={t('dashboard.contractsByStatus')} isLoading={isLoading}>
      <div ref={containerRef} className="w-full h-[150px]">
        {hasData && dimensions ? (
          <PieChart width={dimensions.width} height={dimensions.height}>
            <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={50} label>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        ) : !hasData && !isLoading ? (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
            {t('common.noData')}
          </div>
        ) : null}
      </div>
    </BaseWidget>
  )
}

// Contracts Expiring Soon Widget
export function ContractsExpiringSoonWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'CONTRACTS_EXPIRING_SOON'],
    queryFn: () => dashboardConfigApi.getWidgetData<RecentContract[]>('CONTRACTS_EXPIRING_SOON'),
  })

  const contracts = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.contractsExpiringSoon')}
      icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {contracts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noExpiringContracts')}</p>
        ) : (
          contracts.map((contract) => (
            <Link
              key={contract.contract_id}
              to={`/contracts/${contract.contract_id}`}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{contract.title}</p>
                <p className="text-xs text-muted-foreground truncate">{contract.client_name || '-'}</p>
              </div>
              <div className="text-right ml-2">
                <p className="text-xs text-amber-500 font-medium">
                  {t('dashboard.expiresOn')} {formatDate(contract.end_date || '')}
                </p>
              </div>
            </Link>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

